import { useSearchParams } from 'react-router-dom';
import { initials, type Period } from '@coachrex/shared';
import { useLeaderboard, useMe } from '../lib/api';
import { useRepFilter } from '../lib/repFilter';
import { money, scoreColor } from '../lib/format';
import { ErrorBox, PageHeader, RepFilter, Segmented, Skeleton } from '../components/ui';
import './leaderboard.css';

const PERIODS: { value: Period; label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
];

function speed(min: number | null): string {
  if (min == null) return '—';
  if (min < 60) return `${min}m`;
  if (min < 24 * 60) return `${Math.round(min / 60)}h`;
  return `${Math.round(min / 1440)}d`;
}

/** Handoff screen 06. The rep filter highlights a row instead of hiding the others. */
export function Leaderboard() {
  const [params, setParams] = useSearchParams();
  const period = (PERIODS.some((p) => p.value === params.get('period')) ? params.get('period') : 'week') as Period;
  const { data, error, isLoading } = useLeaderboard(period);
  const rep = useRepFilter();
  const isRep = useMe().data?.user.role === 'rep';
  const rows = data?.rows ?? [];

  return (
    <div className="page">
      <PageHeader
        title="Leaderboard"
        subtitle="Ranked by average rep score. Speed to lead is the average time from a lead’s first message to the first reply on any channel."
        actions={
          <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <RepFilter reps={rows.map((r) => r.rep)} />
            <Segmented label="Period" options={PERIODS} value={period} onChange={(p) => setParams(p === 'week' ? {} : { period: p }, { replace: true })} />
          </span>
        }
      />
      <section className="card card--list">
        <div className={`list-head lb-grid${isRep ? ' lb-grid--rep' : ''}`}>
          <span>#</span>
          <span>Rep</span>
          <span className="r">Score</span>
          {!isRep && <span className="r">Calls</span>}
          <span className="r">Booked rate</span>
          <span className="r">Speed to lead</span>
          <span className="r">Recovered</span>
          <span className="r">Trend</span>
        </div>
        {error ? (
          <div style={{ padding: 12 }}>
            <ErrorBox error={error} />
          </div>
        ) : isLoading ? (
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="list-empty">No calls or new leads in this period.</div>
        ) : (
          rows.map((r, i) => {
            const selected = rep.value === r.rep.id;
            return (
              <div key={r.rep.id} className={`list-row lb-grid${isRep ? ' lb-grid--rep' : ''}${selected ? ' is-selected' : ''}${i === 0 ? ' is-first' : ''}`}>
                <span className={`lb-rank${i === 0 ? ' is-first' : ''}`}>{i + 1}</span>
                <span className="lb-rep">
                  <span className="team-avatar">{initials(r.rep.name).slice(0, 2)}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={{ fontWeight: 600 }}>{r.rep.name}</span>
                    <span className="card-meta">{r.role === 'manager' ? 'Manager' : 'Rep'}</span>
                  </span>
                </span>
                <span className="r" style={{ fontWeight: 600, color: r.score != null ? scoreColor(r.score) : 'var(--faint)' }}>
                  {r.score ?? '—'}
                </span>
                {!isRep && <span className="r">{(r.calls ?? 0).toLocaleString('en-US')}</span>}
                <span className="r" title={`${r.leads} new leads`}>
                  {r.bookedRate == null ? '—' : `${r.bookedRate}%`}
                </span>
                <span className="r">{speed(r.speedToLeadMin)}</span>
                <span className="r">{r.recovered ? money(r.recovered, data!.currency) : '—'}</span>
                <span className="r" style={{ color: r.trend == null ? 'var(--faint)' : r.trend >= 0 ? 'var(--score-good)' : 'var(--score-bad)', fontWeight: 600 }}>
                  {r.trend == null ? '—' : `${r.trend > 0 ? '▲' : r.trend < 0 ? '▼' : ''} ${Math.abs(r.trend)}`}
                </span>
              </div>
            );
          })
        )}
      </section>
      <span className="footnote-text">Scores need AI analysis; until calls are scored, reps are ranked by number of calls. Inactive users are left out.</span>
    </div>
  );
}
